/**
 * Editor project dirty 판정 정책 정본 (STUDIO-PACKAGE-001 / STUDIO-SPACE-003).
 *
 * dirty의 의미는 "서버 package(`.viewr`)에 아직 반영되지 않은 변경이 runtime에 있다"이다.
 * 따라서 dirty를 만드는 것과 해제하는 것을 다음 한 곳에서만 정의한다.
 *
 * dirty로 만드는 것 (사용자 편집):
 *  - Unity transform 편집(gizmo/panel), HMI mutation, Prototype mutation
 *  - Space rename / delete / reorder 같은 사용자 구조 편집
 *  - matching Add completion은 새 active Space의 structural mutation으로 기록
 *
 * dirty를 해제하는 것 (서버 package가 이미 그 상태를 담고 있음):
 *  - 명시적 Save의 matching package terminal success
 *  - legacy 서버 `.viewr`를 다시 쓴 workflow terminal (object import · library attach)
 *  - revision workflow는 matching revision commit terminal
 *  - Undo/Redo/reset으로 저장 baseline이 복원된 경우
 *
 * Unity가 보내는 `isDirty:false`는 그 자체로 저장 baseline을 옮기지 못한다.
 * 위 목록의 changeType만 baseline reset 근거가 된다.
 */

export const EDITOR_MODEL_CHANGED_EVENT = "unity:modelChanged";
export const EDITOR_PACKAGE_BASELINE_CAPTURE_EVENT =
  "viewr:packageBaselineCapture";

export interface EditorPackageBaselineCaptureDetail {
  persistenceIdentity: string;
  spaceIndex: number;
}

export interface EditorPackagePersistedDetail extends EditorPackageBaselineCaptureDetail {
  reason: string;
  terminalIdentity?: string | null;
}

export interface EditorSpaceDirtyBaseline {
  persistenceIdentity: string;
  spaceIndex: number;
  mutationEpoch: number;
}

export interface EditorProjectDirtyLedger {
  markDirty: (spaceIndex: number) => number;
  markDiscarded: (spaceIndex: number) => void;
  captureBaseline: (
    persistenceIdentity: string,
    spaceIndices: number | Iterable<number>,
  ) => EditorSpaceDirtyBaseline[];
  cancelBaseline: (persistenceIdentity: string) => void;
  commitBaseline: (
    persistenceIdentity: string,
    spaceIndex?: number | null,
  ) => number[];
  hasPendingBaseline: (persistenceIdentity: string) => boolean;
  isDirty: (spaceIndex: number) => boolean;
  hasAnyDirty: () => boolean;
  getDirtySpaceIndices: () => number[];
  insertSpace: (spaceIndex: number, markInsertedDirty?: boolean) => void;
  removeSpace: (spaceIndex: number) => void;
  reorderSpace: (fromIndex: number, toIndex: number) => void;
  getMutationEpoch: (spaceIndex: number) => number;
}

function normalizeSpaceIndex(spaceIndex: number): number | null {
  return Number.isInteger(spaceIndex) && spaceIndex >= 0 ? spaceIndex : null;
}

function normalizePersistenceIdentity(identity: string): string {
  return identity.trim();
}

/**
 * Space별 dirty와 package baseline attempt를 함께 소유한다.
 *
 * package terminal은 capture 당시 mutation epoch와 현재 epoch가 같을 때만 clean을
 * 적용한다. 따라서 snapshot/package 작업 중 발생한 더 늦은 사용자 편집은 stale
 * terminal이 지울 수 없다.
 */
export function createEditorProjectDirtyLedger(): EditorProjectDirtyLedger {
  const mutationEpochBySpace = new Map<number, number>();
  const persistedEpochBySpace = new Map<number, number>();
  const pendingBaselines = new Map<string, EditorSpaceDirtyBaseline[]>();

  const getMutationEpoch = (spaceIndex: number): number => {
    const normalizedSpaceIndex = normalizeSpaceIndex(spaceIndex);
    if (normalizedSpaceIndex === null) return 0;
    return mutationEpochBySpace.get(normalizedSpaceIndex) ?? 0;
  };

  const isDirty = (spaceIndex: number): boolean => {
    const normalizedSpaceIndex = normalizeSpaceIndex(spaceIndex);
    if (normalizedSpaceIndex === null) return false;
    return (
      getMutationEpoch(normalizedSpaceIndex) >
      (persistedEpochBySpace.get(normalizedSpaceIndex) ?? 0)
    );
  };

  const remapSpaceIndices = (
    mapIndex: (spaceIndex: number) => number | null,
  ): void => {
    const remapEpochMap = (source: Map<number, number>): void => {
      const remapped = new Map<number, number>();
      source.forEach((epoch, spaceIndex) => {
        const nextIndex = mapIndex(spaceIndex);
        if (nextIndex !== null) remapped.set(nextIndex, epoch);
      });
      source.clear();
      remapped.forEach((epoch, spaceIndex) => source.set(spaceIndex, epoch));
    };

    remapEpochMap(mutationEpochBySpace);
    remapEpochMap(persistedEpochBySpace);
    pendingBaselines.forEach((baselines, identity) => {
      const remapped = baselines.flatMap((baseline) => {
        const nextIndex = mapIndex(baseline.spaceIndex);
        return nextIndex === null
          ? []
          : [{ ...baseline, spaceIndex: nextIndex }];
      });
      if (remapped.length > 0) pendingBaselines.set(identity, remapped);
      else pendingBaselines.delete(identity);
    });
  };

  return {
    markDirty(spaceIndex) {
      const normalizedSpaceIndex = normalizeSpaceIndex(spaceIndex);
      if (normalizedSpaceIndex === null) return 0;
      const nextEpoch = getMutationEpoch(normalizedSpaceIndex) + 1;
      mutationEpochBySpace.set(normalizedSpaceIndex, nextEpoch);
      return nextEpoch;
    },

    markDiscarded(spaceIndex) {
      const normalizedSpaceIndex = normalizeSpaceIndex(spaceIndex);
      if (normalizedSpaceIndex === null) return;
      persistedEpochBySpace.set(
        normalizedSpaceIndex,
        getMutationEpoch(normalizedSpaceIndex),
      );
    },

    captureBaseline(persistenceIdentity, spaceIndices) {
      const normalizedIdentity =
        normalizePersistenceIdentity(persistenceIdentity);
      if (!normalizedIdentity) return [];
      const indices =
        typeof spaceIndices === "number"
          ? [spaceIndices]
          : Array.from(spaceIndices);
      const baselines = Array.from(
        new Set(
          indices
            .map(normalizeSpaceIndex)
            .filter((value): value is number => value !== null),
        ),
      ).map((spaceIndex) => ({
        persistenceIdentity: normalizedIdentity,
        spaceIndex,
        mutationEpoch: getMutationEpoch(spaceIndex),
      }));
      if (baselines.length > 0) {
        pendingBaselines.set(normalizedIdentity, baselines);
      }
      return baselines;
    },

    cancelBaseline(persistenceIdentity) {
      const normalizedIdentity =
        normalizePersistenceIdentity(persistenceIdentity);
      if (!normalizedIdentity) return;
      pendingBaselines.delete(normalizedIdentity);
    },

    commitBaseline(persistenceIdentity, spaceIndex) {
      const normalizedIdentity =
        normalizePersistenceIdentity(persistenceIdentity);
      if (!normalizedIdentity) return [];
      const pending = pendingBaselines.get(normalizedIdentity);
      if (!pending) return [];

      const normalizedSpaceIndex =
        typeof spaceIndex === "number" ? normalizeSpaceIndex(spaceIndex) : null;
      const matched = pending.filter(
        (baseline) =>
          normalizedSpaceIndex === null ||
          baseline.spaceIndex === normalizedSpaceIndex,
      );
      const remaining = pending.filter(
        (baseline) => !matched.includes(baseline),
      );
      if (remaining.length > 0) {
        pendingBaselines.set(normalizedIdentity, remaining);
      } else {
        pendingBaselines.delete(normalizedIdentity);
      }

      const cleaned: number[] = [];
      matched.forEach((baseline) => {
        if (getMutationEpoch(baseline.spaceIndex) !== baseline.mutationEpoch) {
          return;
        }
        persistedEpochBySpace.set(baseline.spaceIndex, baseline.mutationEpoch);
        cleaned.push(baseline.spaceIndex);
      });
      return cleaned;
    },

    hasPendingBaseline(persistenceIdentity) {
      const normalizedIdentity =
        normalizePersistenceIdentity(persistenceIdentity);
      return Boolean(
        normalizedIdentity && pendingBaselines.has(normalizedIdentity),
      );
    },

    isDirty,

    hasAnyDirty() {
      return Array.from(mutationEpochBySpace.keys()).some(isDirty);
    },

    getDirtySpaceIndices() {
      return Array.from(mutationEpochBySpace.keys()).filter(isDirty);
    },

    insertSpace(spaceIndex, markInsertedDirty = false) {
      const normalizedSpaceIndex = normalizeSpaceIndex(spaceIndex);
      if (normalizedSpaceIndex === null) return;
      remapSpaceIndices((existingIndex) =>
        existingIndex >= normalizedSpaceIndex
          ? existingIndex + 1
          : existingIndex,
      );
      if (markInsertedDirty) {
        const nextEpoch = getMutationEpoch(normalizedSpaceIndex) + 1;
        mutationEpochBySpace.set(normalizedSpaceIndex, nextEpoch);
      }
    },

    removeSpace(spaceIndex) {
      const normalizedSpaceIndex = normalizeSpaceIndex(spaceIndex);
      if (normalizedSpaceIndex === null) return;
      remapSpaceIndices((existingIndex) => {
        if (existingIndex === normalizedSpaceIndex) return null;
        return existingIndex > normalizedSpaceIndex
          ? existingIndex - 1
          : existingIndex;
      });
    },

    reorderSpace(fromIndex, toIndex) {
      const normalizedFromIndex = normalizeSpaceIndex(fromIndex);
      const normalizedToIndex = normalizeSpaceIndex(toIndex);
      if (
        normalizedFromIndex === null ||
        normalizedToIndex === null ||
        normalizedFromIndex === normalizedToIndex
      ) {
        return;
      }
      remapSpaceIndices((existingIndex) => {
        if (existingIndex === normalizedFromIndex) return normalizedToIndex;
        if (
          normalizedFromIndex < normalizedToIndex &&
          existingIndex > normalizedFromIndex &&
          existingIndex <= normalizedToIndex
        ) {
          return existingIndex - 1;
        }
        if (
          normalizedToIndex < normalizedFromIndex &&
          existingIndex >= normalizedToIndex &&
          existingIndex < normalizedFromIndex
        ) {
          return existingIndex + 1;
        }
        return existingIndex;
      });
    },

    getMutationEpoch,
  };
}

/**
 * 서버 package가 이미 현재 runtime 상태를 담고 있음을 알리는 clean baseline changeType.
 *
 * object import·library attach는 mesh를 붙이기 전에 전체 ViewRData VDB snapshot을
 * 업로드하고, Converter가 그 VDB로 `.viewr`를 다시 패키징한다. 즉 terminal success
 * 시점에는 그때까지의 runtime 변경이 서버 package에 이미 들어가 있으므로 unsaved가
 * 아니다.
 */
export const SERVER_PERSISTED_BASELINE_CHANGE_TYPE =
  "server_persisted_baseline";

const BASELINE_RESET_CHANGE_TYPES: ReadonlySet<string> = new Set([
  "undo",
  "redo",
  "reset",
  "baseline_set",
  SERVER_PERSISTED_BASELINE_CHANGE_TYPE,
]);

/** `isDirty:false` event가 저장 baseline을 갱신할 수 있는 changeType인지 판정한다. */
export function isDirtyBaselineResetChangeType(
  changeType?: string | null,
): boolean {
  if (typeof changeType !== "string") return false;
  const normalizedChangeType = changeType.trim();
  return (
    BASELINE_RESET_CHANGE_TYPES.has(normalizedChangeType) ||
    normalizedChangeType.startsWith("undo_") ||
    normalizedChangeType.startsWith("redo_") ||
    normalizedChangeType.startsWith("reset_")
  );
}

/** 이 changeType이 payload transform을 새 저장 baseline snapshot으로 쓸 수 있는지. */
export function canAdoptChangeTypePayloadAsBaseline(
  changeType?: string | null,
): boolean {
  return (
    isDirtyBaselineResetChangeType(changeType) &&
    changeType?.trim() !== SERVER_PERSISTED_BASELINE_CHANGE_TYPE
  );
}

export function markEditorProjectDirty(
  changeType: string,
  spaceIndex?: number | null,
): void {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(EDITOR_MODEL_CHANGED_EVENT, {
      detail: {
        isDirty: true,
        changeType,
        ...(typeof spaceIndex === "number" ? { spaceIndex } : {}),
      },
    }),
  );
}

/**
 * 서버 package terminal로 이미 영속화된 상태를 clean baseline으로 승격한다.
 *
 * 사용자가 추가로 편집하지 않았는데 Space 전환·Home 이동에서 Save 확인 팝업이
 * 뜨지 않도록, 서버 쓰기가 끝난 workflow terminal에서만 호출한다.
 */
export function captureEditorProjectPackageBaseline(
  detail: EditorPackageBaselineCaptureDetail,
): void {
  if (typeof window === "undefined") return;

  const persistenceIdentity = normalizePersistenceIdentity(
    detail.persistenceIdentity,
  );
  const spaceIndex = normalizeSpaceIndex(detail.spaceIndex);
  if (!persistenceIdentity || spaceIndex === null) return;

  window.dispatchEvent(
    new CustomEvent(EDITOR_PACKAGE_BASELINE_CAPTURE_EVENT, {
      detail: { persistenceIdentity, spaceIndex },
    }),
  );
}

export function markEditorProjectClean(
  detail: EditorPackagePersistedDetail,
): void {
  if (typeof window === "undefined") return;

  const persistenceIdentity = normalizePersistenceIdentity(
    detail.persistenceIdentity,
  );
  const spaceIndex = normalizeSpaceIndex(detail.spaceIndex);
  if (!persistenceIdentity || spaceIndex === null) return;

  window.dispatchEvent(
    new CustomEvent(EDITOR_MODEL_CHANGED_EVENT, {
      detail: {
        isDirty: false,
        changeType: SERVER_PERSISTED_BASELINE_CHANGE_TYPE,
        reason: detail.reason,
        persistenceIdentity,
        spaceIndex,
        terminalIdentity: detail.terminalIdentity?.trim() || null,
      },
    }),
  );
}
